import React, { useState, useMemo } from "react";
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    IconButton,
    Stack,
    Typography,
    Divider,
    Alert,
    Grid,
    MenuItem,
    TextField,
} from "@mui/material";
import { ChevronDown, ChevronUp, Building2, Calendar, Shield, AlertCircle, CheckCircle2, UserCog } from "lucide-react";
import { PAPEL_NO_VINCULO_OPTIONS, SITUACAO_DO_VINCULO_OPTIONS } from "../usersUtils";

const COR_DA_SITUACAO = { ATIVO: 'success', DESATIVADO: 'default', BLOQUEADO: 'error' };

/**
 * LicenseRoleCard - Displays a license with its contextual roles
 * Implements the License-first UX pattern required by the security model
 */
export default function LicenseRoleCard({
    license,
    availableRoles = [],
    selectedRoles = [],
    onRolesChange,
    onRemoveLicense,
    vinculo,
    onVinculoChange,
}) {
    const [expanded, setExpanded] = useState(true);

    // Filter roles that belong to this specific license
    const licenseRoles = useMemo(() => {
        return availableRoles.filter(role => 
            (role.licenseId || role.license?.id) === (license.id || license.licenseId)
        );
    }, [availableRoles, license]);

    // Check which roles are currently selected for this license
    const selectedRoleIds = useMemo(() => {
        return selectedRoles.map(r => r.roleId || r.id);
    }, [selectedRoles]);

    const handleRoleToggle = (role) => {
        const roleId = role.roleId || role.id;
        const isSelected = selectedRoleIds.includes(roleId);

        if (isSelected) {
            // Remove role
            onRolesChange(selectedRoles.filter(r => (r.roleId || r.id) !== roleId));
        } else {
            // Add role
            onRolesChange([...selectedRoles, role]);
        }
    };

    // License metadata
    const companyName = license?.customer?.legalName || license?.empresa?.razao_social || license?.empresa?.nome || 'Empresa não informada';
    const planName = license?.plan?.productPlanName || license?.plano?.nome_plano || 'Plano';
    const tenantId = license?.tenantId || license?.tenant_id || '-';
    const systemName = license?.system?.systemName || license?.sistema?.nome || '-';
    const validUntil = license?.validUntil || license?.validade;
    const isActive = license?.active ?? license?.ativo ?? true;
    const selectedCount = licenseRoles.filter(r => selectedRoleIds.includes(r.roleId || r.id)).length;

    // Status badge color
    const getStatusColor = () => {
        if (!isActive) return 'error';
        if (validUntil) {
            const daysUntilExpiry = Math.floor((new Date(validUntil) - new Date()) / (1000 * 60 * 60 * 24));
            if (daysUntilExpiry < 0) return 'error';
            if (daysUntilExpiry < 30) return 'warning';
        }
        return 'success';
    };

    return (
        <Card 
            variant="outlined" 
            sx={{ 
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
                transition: 'all 0.2s ease',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                {/* Card Header */}
                <Box 
                    sx={{ 
                        px: 3, 
                        py: 2.5,
                        bgcolor: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        cursor: 'pointer',
                    }}
                    onClick={() => setExpanded(!expanded)}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: 2,
                                        bgcolor: '#0f766e',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Building2 size={18} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {planName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {companyName}
                                    </Typography>
                                </Box>
                            </Stack>
                            
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip 
                                    label={isActive ? 'Ativa' : 'Inativa'} 
                                    color={getStatusColor()}
                                    size="small"
                                    icon={isActive ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                                />
                                {vinculo && (
                                    <Chip
                                        label={`${vinculo.papel === 'ADMINISTRADOR' ? 'Administrador' : 'Usuário'} · ${(SITUACAO_DO_VINCULO_OPTIONS.find((o) => o.value === vinculo.situacao) || {}).label || vinculo.situacao}`}
                                        color={COR_DA_SITUACAO[vinculo.situacao] || 'default'}
                                        variant="outlined"
                                        size="small"
                                        icon={<UserCog size={14} />}
                                    />
                                )}
                                {selectedCount > 0 && (
                                    <Chip 
                                        label={`${selectedCount} role(s) selecionada(s)`}
                                        size="small"
                                        sx={{ bgcolor: '#dcfce7', color: '#16a34a' }}
                                        icon={<Shield size={14} />}
                                    />
                                )}
                            </Stack>
                        </Box>

                        <IconButton size="small">
                            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </IconButton>
                    </Stack>
                </Box>

                {/* Card Body - Expandable */}
                <Collapse in={expanded}>
                    <Box sx={{ p: 3 }}>
                        {/* License Details */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                                DETALHES DA LICENÇA
                            </Typography>
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Tenant ID:</Typography>
                                    <Typography variant="body2" fontWeight={600}>{tenantId}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">Sistema:</Typography>
                                    <Typography variant="body2" fontWeight={600}>{systemName}</Typography>
                                </Stack>
                                {validUntil && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">Validade:</Typography>
                                        <Chip 
                                            label={new Date(validUntil).toLocaleDateString('pt-BR')}
                                            size="small"
                                            color={getStatusColor()}
                                            icon={<Calendar size={14} />}
                                        />
                                    </Stack>
                                )}
                            </Stack>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Vínculo nesta assinatura (BBF-67) */}
                        {vinculo && onVinculoChange && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                                    VÍNCULO NESTA ASSINATURA
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Papel"
                                            value={vinculo.papel}
                                            onChange={(event) => onVinculoChange({ papel: event.target.value })}
                                            helperText={(PAPEL_NO_VINCULO_OPTIONS.find((o) => o.value === vinculo.papel) || {}).description}
                                            inputProps={{ 'data-testid': 'vinculo-papel' }}
                                        >
                                            {PAPEL_NO_VINCULO_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Situação"
                                            value={vinculo.situacao}
                                            onChange={(event) => onVinculoChange({ situacao: event.target.value })}
                                            helperText="Vale só nesta assinatura."
                                            inputProps={{ 'data-testid': 'vinculo-situacao' }}
                                        >
                                            {SITUACAO_DO_VINCULO_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label="Válido até"
                                            value={vinculo.validoAte || ''}
                                            onChange={(event) => onVinculoChange({ validoAte: event.target.value })}
                                            InputLabelProps={{ shrink: true }}
                                            helperText="Em branco: sem prazo."
                                            inputProps={{ 'data-testid': 'vinculo-validade' }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Roles Section */}
                        <Box>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                                ROLES DISPONÍVEIS NESTA LICENÇA
                            </Typography>
                            
                            {licenseRoles.length === 0 ? (
                                <Alert severity="info" icon={<AlertCircle size={16} />}>
                                    Nenhuma role disponível para esta licença
                                </Alert>
                            ) : (
                                <Stack spacing={1}>
                                    {licenseRoles.map((role) => {
                                        const roleId = role.roleId || role.id;
                                        const isSelected = selectedRoleIds.includes(roleId);
                                        
                                        return (
                                            <Box
                                                key={roleId}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    border: '1px solid',
                                                    borderColor: isSelected ? '#0f766e' : '#e5e7eb',
                                                    bgcolor: isSelected ? '#f0fdfa' : '#ffffff',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        borderColor: '#0f766e',
                                                        bgcolor: '#f0fdfa',
                                                    },
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={() => handleRoleToggle(role)}
                                                            sx={{
                                                                color: '#0f766e',
                                                                '&.Mui-checked': {
                                                                    color: '#0f766e',
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {role.roleName || role.nome || 'Role sem nome'}
                                                            </Typography>
                                                            {role.description && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {role.description}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    sx={{ m: 0, width: '100%' }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>

                        {/* Remove License Button */}
                        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                            <Typography
                                variant="body2"
                                color="error"
                                sx={{ 
                                    cursor: 'pointer', 
                                    '&:hover': { textDecoration: 'underline' },
                                    fontWeight: 600,
                                }}
                                onClick={onRemoveLicense}
                            >
                                Remover esta licença e suas roles
                            </Typography>
                        </Box>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}
